/* PDP v2 (product.ironless_v2). Gallery: swipe is native (CSS scroll-snap); JS only syncs thumbnails and arrows. */

class PdpGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.thumbsBox = this.querySelector('[data-thumbs]');
    this.thumbs = this.querySelectorAll('[data-thumb]');
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.count = this.track.children.length;

    this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));
    this.prev?.addEventListener('click', () => this.goTo(this.index - 1));
    this.next?.addEventListener('click', () => this.goTo(this.index + 1));
    this.track.addEventListener('scroll', () => this.update(), { passive: true });

    this.update();
  }

  goTo(i, behavior = 'smooth') {
    this.track.scrollTo({ left: i * this.track.clientWidth, behavior });
  }

  goToMedia(mediaId, behavior) {
    const slides = [...this.track.children];
    const i = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (i > -1) this.goTo(i, behavior);
  }

  update() {
    const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
    if (i === this.index) return;
    this.index = i;

    this.thumbs.forEach((thumb, n) => thumb.classList.toggle('is-active', n === i));
    [...this.track.children].forEach((slide, n) => {
      if (slide.tagName !== 'VIDEO') return;
      if (n === i) slide.play().catch(() => {});
      else slide.pause();
    });
    if (this.prev) this.prev.disabled = i === 0;
    if (this.next) this.next.disabled = i === this.count - 1;

    const thumb = this.thumbs[i];
    if (thumb) {
      this.thumbsBox.scrollTo({
        left: thumb.offsetLeft - (this.thumbsBox.clientWidth - thumb.clientWidth) / 2,
        behavior: 'smooth',
      });
    }
  }
}

if (!customElements.get('pdp-gallery')) customElements.define('pdp-gallery', PdpGallery);

/* Variant picker: option 1 = size buttons, option 2 = quantity offer cards */
class PdpVariantPicker extends HTMLElement {
  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
    this.input = this.querySelector('[data-variant-input]');
    this.gallery = this.closest('.pdp-section')?.querySelector('pdp-gallery');
    this.addEventListener('change', (event) => {
      this.update();
      if (event.target.dataset.option === '1') this.showSizeImage('smooth');
    });
    this.showSizeImage('instant');
    this.update();
  }

  // Sticky bar text: "<quantity title> · <size>" and "<price> · <save> · <note>"
  updateSticky(selected, size, available) {
    const sticky = this.closest('.pdp-section')?.querySelector('.pdp-sticky');
    if (!sticky) return;

    const offerTitle = this.querySelector('.pdp-offer:has([data-option="2"]:checked) .pdp-offer__title')?.textContent.trim();
    const title = [offerTitle, size].filter(Boolean).join(' · ');
    if (title) sticky.querySelector('[data-sticky-title]').textContent = title;

    const save = selected?.save ? this.dataset.saveText.replace('[percent]', selected.save) : '';
    sticky.querySelector('[data-sticky-meta]').textContent = [selected?.price, save, this.dataset.stickyNote]
      .filter(Boolean)
      .join(' · ');

    const button = sticky.querySelector('[data-sticky-button]');
    button.disabled = !available;
    if (!available) button.textContent = this.dataset.soldOutText;
    else if (selected.save && this.dataset.stickyButtonText) button.textContent = this.dataset.stickyButtonText.replace('[percent]', selected.save);
    else button.textContent = this.dataset.atcText;
  }

  // Scroll the gallery to the image of the selected size (variant featured image)
  showSizeImage(behavior) {
    const size = this.querySelector('[data-option="1"]:checked')?.value;
    const variant = this.variants.find((v) => v.option1 === size && v.media);
    if (variant) this.gallery?.goToMedia?.(variant.media, behavior);
  }

  update() {
    const size = this.querySelector('[data-option="1"]:checked')?.value;
    const qty = this.querySelector('[data-option="2"]:checked')?.value;
    const find = (option2) => this.variants.find((v) => v.option1 === size && (option2 === undefined || v.option2 === option2));

    this.querySelectorAll('[data-offer]').forEach((offer) => {
      const v = find(offer.dataset.offer);
      offer.classList.toggle('is-unavailable', !v || !v.available);
      if (!v) return;

      offer.querySelector('[data-price]').textContent = v.price;

      const compare = offer.querySelector('[data-compare]');
      compare.textContent = v.compare || '';
      compare.hidden = !v.compare;

      const save = offer.querySelector('[data-save]');
      save.textContent = this.dataset.saveText.replace('[percent]', v.save);
      save.hidden = !v.save;
    });

    // Products without options (only the default variant) have no radios: use that variant
    const selected = this.querySelector('[data-option]') ? find(qty) : this.variants[0];
    const available = Boolean(selected?.available);
    const atc = this.querySelector('[data-atc]');
    if (atc) {
      atc.disabled = !available;
      atc.querySelector('[data-atc-text]').textContent = available ? this.dataset.atcText : this.dataset.soldOutText;
    }
    this.updateSticky(selected, size, available);

    if (selected) this.input.value = selected.id;
  }
}

if (!customElements.get('pdp-variant-picker')) customElements.define('pdp-variant-picker', PdpVariantPicker);

/* Delivery info: "Ships by <date>", counted from today in the visitor's browser */
class PdpDelivery extends HTMLElement {
  connectedCallback() {
    const date = new Date();
    const skipWeekends = this.dataset.skipWeekends === 'true';
    const isWeekend = (d) => d.getDay() === 0 || d.getDay() === 6;
    let days = Number(this.dataset.days) || 0;

    while (skipWeekends && isWeekend(date)) date.setDate(date.getDate() + 1);
    while (days > 0) {
      date.setDate(date.getDate() + 1);
      if (!skipWeekends || !isWeekend(date)) days--;
    }

    const options = { weekday: 'long', month: 'long', day: 'numeric' };
    let text;
    try {
      text = date.toLocaleDateString(this.dataset.locale || undefined, options);
    } catch {
      text = date.toLocaleDateString(undefined, options);
    }
    this.querySelector('[data-date]').textContent = text;
  }
}

if (!customElements.get('pdp-delivery')) customElements.define('pdp-delivery', PdpDelivery);

/* Description tabs: smooth open/close, only one open at a time */
class PdpTabs extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll('.pdp-tab__title').forEach((summary) => {
      summary.addEventListener('click', (event) => {
        event.preventDefault();
        const tab = summary.parentElement;
        if (tab.open) {
          this.close(tab);
        } else {
          this.querySelectorAll('.pdp-tab[open]').forEach((openTab) => this.close(openTab));
          this.open(tab);
        }
      });
    });
  }

  animate(tab, from, to, onFinish) {
    const content = tab.querySelector('.pdp-tab__content');
    const duration = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 300;
    tab.animation?.cancel();
    tab.animation = content.animate({ height: [from, to] }, { duration, easing: 'ease' });
    tab.animation.onfinish = () => {
      tab.animation = null;
      onFinish?.();
    };
  }

  open(tab) {
    tab.open = true;
    tab.classList.add('is-open');
    const content = tab.querySelector('.pdp-tab__content');
    this.animate(tab, '0px', `${content.scrollHeight}px`);
  }

  close(tab) {
    tab.classList.remove('is-open');
    const content = tab.querySelector('.pdp-tab__content');
    this.animate(tab, `${content.offsetHeight}px`, '0px', () => {
      tab.open = false;
    });
  }
}

if (!customElements.get('pdp-tabs')) customElements.define('pdp-tabs', PdpTabs);

/* Sticky add to cart, clicks the main button.
   Desktop: visible once the main button has scrolled above the viewport.
   Mobile: visible whenever the main button is not in the viewport (also on load). */
class PdpStickyAtc extends HTMLElement {
  connectedCallback() {
    this.mainButton = this.closest('.pdp-section')?.querySelector('[data-atc]');
    if (!this.mainButton) return;

    this.querySelector('[data-sticky-button]').addEventListener('click', () => this.mainButton.click());

    this.observer = new IntersectionObserver(([entry]) => {
      const isMobile = window.matchMedia('(max-width: 749px)').matches;
      const visible = !entry.isIntersecting && (isMobile || entry.boundingClientRect.top < 0);
      this.classList.toggle('is-visible', visible);
      this.setAttribute('aria-hidden', String(!visible));
      this.querySelector('[data-sticky-button]').tabIndex = visible ? 0 : -1;
    });
    this.observer.observe(this.mainButton);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('pdp-sticky-atc')) customElements.define('pdp-sticky-atc', PdpStickyAtc);

/* Rating badge: scroll to the Judge.me reviews widget */
const scrollToReviews = (event) => {
  if (!event.target.closest('.pdp-rating')) return;
  if (event.type === 'keydown' && event.key !== 'Enter' && event.key !== ' ') return;
  const reviews = document.querySelector('.jdgm-review-widget');
  if (!reviews) return;
  event.preventDefault();
  window.scrollTo({ top: reviews.getBoundingClientRect().top + window.scrollY - 20, behavior: 'smooth' });
};
document.addEventListener('click', scrollToReviews);
document.addEventListener('keydown', scrollToReviews);
